
import React, { useState, useMemo, useEffect, useRef } from 'react';
import { X, ArrowUpRight, ArrowDownRight, TrendingUp, ChevronUp, ChevronDown, ChevronsUpDown } from 'lucide-react';

interface SummaryModalProps {
  isOpen: boolean;
  onClose: () => void;
  data: any[];
  theme: 'dark' | 'light';
  icons?: Record<string, string>;
}

type SortOrder = 'asc' | 'desc' | null;

interface SortState {
  key: string | null;
  order: SortOrder;
}

const SummaryModal: React.FC<SummaryModalProps> = ({ isOpen, onClose, data, theme, icons = {} }) => {
  const [sortState, setSortState] = useState<SortState>({ key: null, order: null });
  
  // State for animation of total value
  const [valueChange, setValueChange] = useState<'up' | 'down' | 'neutral'>('neutral');
  
  // Calculate totals
  const totalInvestment = useMemo(() => data.reduce((acc, curr) => acc + (curr.totalBuy || 0), 0), [data]);
  const totalValue = useMemo(() => data.reduce((acc, curr) => acc + (curr.currentVal || 0), 0), [data]);
  
  const prevValueRef = useRef(totalValue);

  // Effect to detect price changes and trigger animation
  useEffect(() => {
    if (totalValue > prevValueRef.current) {
        setValueChange('up');
        const timer = setTimeout(() => setValueChange('neutral'), 1000);
        return () => clearTimeout(timer);
    } else if (totalValue < prevValueRef.current) {
        setValueChange('down');
        const timer = setTimeout(() => setValueChange('neutral'), 1000);
        return () => clearTimeout(timer);
    }
    prevValueRef.current = totalValue;
  }, [totalValue]);

  const handleSort = (key: string) => {
    setSortState(prev => {
      if (prev.key !== key) return { key, order: 'desc' };
      if (prev.order === 'desc') return { key, order: 'asc' };
      return { key: null, order: null };
    });
  };

  const sortedData = useMemo(() => {
    if (!sortState.key || !sortState.order) return data;

    return [...data].sort((a, b) => {
      const aVal = a[sortState.key!];
      const bVal = b[sortState.key!];

      if (typeof aVal === 'string') {
        return sortState.order === 'asc' 
          ? aVal.localeCompare(bVal) 
          : bVal.localeCompare(aVal);
      }

      return sortState.order === 'asc' ? aVal - bVal : bVal - aVal;
    });
  }, [data, sortState]);

  if (!isOpen) return null;

  const getIconUrl = (symbol: string) => {
    const base = symbol.split('-')[0].toUpperCase();
    return icons[base] || '';
  };

  const themeClasses = {
    overlay: "fixed inset-0 bg-black/80 backdrop-blur-sm z-[100] flex items-center justify-center p-2 sm:p-4 animate-in fade-in duration-200",
    container: `${theme === 'dark' ? 'bg-[#0f0f0f] border-[#262626]' : 'bg-white border-slate-200'} border w-full max-w-[95vw] lg:max-w-7xl h-[90vh] rounded-2xl shadow-2xl overflow-hidden flex flex-col animate-in zoom-in-95 duration-200`,
    header: `flex items-center justify-between p-5 border-b ${theme === 'dark' ? 'border-[#262626]' : 'border-slate-100'}`,
    tableHeader: `${theme === 'dark' ? 'bg-[#1a1a1a] text-slate-400' : 'bg-slate-50 text-slate-500'} text-xs font-black uppercase tracking-wider sticky top-0 z-10`,
    cell: `px-3 py-3 text-sm font-medium border-b ${theme === 'dark' ? 'border-[#1a1a1a]' : 'border-slate-50'} whitespace-nowrap`,
    textMain: theme === 'dark' ? 'text-white' : 'text-slate-900',
    textMuted: theme === 'dark' ? 'text-slate-500' : 'text-slate-400'
  };

  const HeaderCell = ({ label, sortKey, align = 'left' }: { label: string, sortKey: string, align?: 'left' | 'center' | 'right' }) => {
    const isActive = sortState.key === sortKey;
    return (
      <th 
        className={`${themeClasses.tableHeader} px-3 py-3 cursor-pointer hover:text-indigo-500 transition-colors group`}
        onClick={() => handleSort(sortKey)}
      >
        <div className={`flex items-center space-x-1 ${align === 'center' ? 'justify-center' : align === 'right' ? 'justify-end' : 'justify-start'}`}>
          <span>{label}</span>
          <div className={`${isActive ? 'text-indigo-500' : 'text-slate-600 opacity-0 group-hover:opacity-100'} transition-all`}>
            {isActive ? (
              sortState.order === 'desc' ? <ChevronDown size={12} /> : <ChevronUp size={12} />
            ) : (
              <ChevronsUpDown size={12} />
            )}
          </div>
        </div>
      </th>
    );
  };

  const getValueColorClass = () => {
    if (valueChange === 'up') return 'text-[#4ade80] scale-105';
    if (valueChange === 'down') return 'text-[#f87171] scale-105';
    return themeClasses.textMain;
  };

  return (
    <div className={themeClasses.overlay}>
      <div className={themeClasses.container}>
        <div className={themeClasses.header}>
          <div className="flex items-center space-x-3">
            <div className="bg-indigo-600 p-2 rounded-lg shadow-lg shadow-indigo-600/20">
              <TrendingUp size={20} className="text-white" />
            </div>
            <div>
              <h2 className={`text-xl font-black ${themeClasses.textMain} tracking-tight uppercase`}>Reporte Operativo</h2>
              <p className={themeClasses.textMuted + " text-[10px] font-bold uppercase"}>Métricas detalladas de cartera</p>
            </div>
          </div>
          <button onClick={onClose} className={`p-2 rounded-full ${theme === 'dark' ? 'hover:bg-white/10' : 'hover:bg-slate-100'} transition-colors`}>
            <X size={24} className={themeClasses.textMain} />
          </button>
        </div>

        <div className="flex-1 overflow-auto">
          <table className="w-full text-left border-collapse table-auto">
            <thead className={themeClasses.tableHeader}>
              <tr>
                <HeaderCell label="$ Venta" sortKey="totalSell" />
                <HeaderCell label="$ Inv." sortKey="totalBuy" />
                <HeaderCell label="$ Act." sortKey="currentVal" />
                <HeaderCell label="Sym" sortKey="ticket" />
                <HeaderCell label="🔴 Precio" sortKey="currentPrice" align="center" />
                <HeaderCell label="$ Prom." sortKey="averageBuyPrice" />
                <HeaderCell label="Cant." sortKey="qtyBuy" />
                <HeaderCell label="C. Venta" sortKey="qtySell" />
                <HeaderCell label="% Liq." sortKey="ventasPerc" align="center" />
                <HeaderCell label="$ ROI" sortKey="totalProfit" align="right" />
                <HeaderCell label="% ROI" sortKey="roi" align="right" />
              </tr>
            </thead>
            <tbody className={theme === 'dark' ? 'divide-[#1a1a1a]' : 'divide-slate-50'}>
              {sortedData.map((m) => (
                <tr key={m.ticket} className={`${theme === 'dark' ? 'hover:bg-[#161616]' : 'hover:bg-slate-50/50'} transition-colors group`}>
                  <td className={`${themeClasses.cell} text-slate-400 font-mono`}>
                    {m.totalSell > 0 ? (m.totalSell || 0).toLocaleString(undefined, { minimumFractionDigits: 2 }) : '-'}
                  </td>
                  <td className={`${themeClasses.cell} ${themeClasses.textMain} font-mono font-bold`}>
                    {(m.totalBuy || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                  <td className={`${themeClasses.cell} ${themeClasses.textMain} font-mono font-bold`}>
                    {(m.currentVal || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                  <td className={themeClasses.cell}>
                    <div className="flex items-center space-x-2">
                      <div className={`w-6 h-6 rounded-full flex items-center justify-center text-[10px] font-black relative flex-shrink-0 overflow-hidden ${theme === 'dark' ? 'bg-indigo-500/20 text-indigo-400' : 'bg-indigo-50 text-indigo-600'}`}>
                        <span className="uppercase">{m.ticket.charAt(0)}</span>
                        {getIconUrl(m.ticket) && (
                          <img 
                            src={getIconUrl(m.ticket)} 
                            className="absolute inset-0 w-full h-full object-contain bg-inherit" 
                            style={{ imageRendering: '-webkit-optimize-contrast' }}
                            onError={(e) => (e.currentTarget.style.display = 'none')}
                            alt=""
                          />
                        )}
                      </div>
                      <span className="px-2 py-0.5 bg-indigo-600/10 text-indigo-500 rounded font-black text-xs uppercase">
                        {m.ticket.split('-')[0]}
                      </span>
                    </div>
                  </td>
                  <td className={`${themeClasses.cell} text-center font-mono text-indigo-400 font-bold`}>
                    {(m.currentPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                  <td className={`${themeClasses.cell} ${themeClasses.textMuted} font-mono`}>
                    {(m.averageBuyPrice || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                  <td className={`${themeClasses.cell} font-mono ${themeClasses.textMain}`}>
                    {(m.qtyBuy || 0).toLocaleString(undefined, { maximumFractionDigits: 6 })}
                  </td>
                  <td className={`${themeClasses.cell} font-mono text-orange-500`}>
                    {m.qtySell > 0 ? (m.qtySell || 0).toLocaleString(undefined, { maximumFractionDigits: 6 }) : '0'}
                  </td>
                  <td className={`${themeClasses.cell} text-center font-bold`}>
                    <div className="flex flex-col items-center">
                      <div className="w-12 bg-slate-800 rounded-full h-1 mb-0.5 hidden sm:block">
                        <div className="bg-orange-500 h-1 rounded-full" style={{ width: `${Math.min(m.ventasPerc || 0, 100)}%` }}></div>
                      </div>
                      <span className="text-[10px]">{(m.ventasPerc || 0).toFixed(1)}%</span>
                    </div>
                  </td>
                  <td className={`${themeClasses.cell} text-right font-mono font-bold ${m.totalProfit >= 0 ? 'text-[#4ade80]' : 'text-[#f87171]'}`}>
                    {m.totalProfit >= 0 ? '+' : ''}{(m.totalProfit || 0).toLocaleString(undefined, { minimumFractionDigits: 2 })}
                  </td>
                  <td className={`${themeClasses.cell} text-right font-black ${m.roi >= 0 ? 'text-[#4ade80]' : 'text-[#f87171]'}`}>
                    <div className="flex items-center justify-end space-x-1">
                      <span>{m.roi >= 0 ? '+' : ''}{(m.roi || 0).toFixed(1)}%</span>
                      {m.roi >= 0 ? <ArrowUpRight size={12} /> : <ArrowDownRight size={12} />}
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        
        <div className={`p-4 ${theme === 'dark' ? 'bg-[#161616]' : 'bg-slate-50'} border-t ${theme === 'dark' ? 'border-[#262626]' : 'border-slate-100'} flex justify-between items-center`}>
          <p className="text-[9px] font-bold text-slate-500 uppercase tracking-widest flex items-center space-x-2">
             <span className="w-2 h-2 rounded-full bg-green-500 animate-pulse"></span>
             <span>Reporte consolidado • Tiempo Real</span>
          </p>
          <div className="flex space-x-6">
            <div className="text-right">
              <span className="block text-[9px] font-bold text-slate-500 uppercase">Inversión Total</span>
              <span className={`text-sm font-black ${themeClasses.textMain}`}>${totalInvestment.toLocaleString()}</span>
            </div>
            <div className="text-right">
              <span className="block text-[9px] font-bold text-slate-500 uppercase">Valor Actual</span>
              <span className={`text-sm font-black transition-all duration-300 ${getValueColorClass()}`}>${totalValue.toLocaleString()}</span>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default SummaryModal;
